/**
 * Reine Tastaturnavigation der Vorschau-Matrix (Punkt 9, WR-13, 35.1-06). Kein React-, DOM- oder
 * Formularbezug -- die Komponente (`EmissionReportMatrix.tsx`) haelt den Fokuszustand
 * (`focusCell`) und ruft diese Funktionen mit dem aktuellen Raster auf.
 *
 * Eine Zelle ist ueber `rowKey` (Zeile) und `position` (Spalte, der ORIGINALE Index im
 * `measurements`-Array, siehe `matrixEdits.ts`/Punkt 4) eindeutig bestimmt. Zeilenschluessel in
 * Anzeigereihenfolge: `value:<fieldKey>` je Merkmal, in der Reihenfolge der Stoffgruppen.
 */

export interface MatrixCellRef {
  rowKey: string;
  position: number;
}

export interface MatrixGrid {
  /** Zeilenschluessel in Anzeigereihenfolge (`value:<fieldKey>`, ...). */
  rowKeys: readonly string[];
  /** Ob die Messung an dieser Position ausgeschlossen ist (Punkt 4) -- Index = `position`. */
  excluded: readonly boolean[];
}

function isSelectablePosition(grid: MatrixGrid, position: number): boolean {
  return position >= 0 && position < grid.excluded.length && !grid.excluded[position];
}

/** Naechste (bzw. vorherige) nicht ausgeschlossene Position ab `from` (exklusiv), oder `null`. */
function nextSelectablePosition(grid: MatrixGrid, from: number, direction: 1 | -1): number | null {
  for (let position = from + direction; position >= 0 && position < grid.excluded.length; position += direction) {
    if (!grid.excluded[position]) {
      return position;
    }
  }
  return null;
}

/** Erste nicht ausgeschlossene Position im Raster, oder `null`, wenn keine existiert. */
function firstSelectablePosition(grid: MatrixGrid): number | null {
  const index = grid.excluded.findIndex((excludedFlag) => !excludedFlag);
  return index === -1 ? null : index;
}

/** Erste Zeile und erste nicht ausgeschlossene Position, oder `null`, wenn keine existiert. */
export function firstCell(grid: MatrixGrid): MatrixCellRef | null {
  const rowKey = grid.rowKeys[0];
  if (rowKey === undefined) {
    return null;
  }
  const position = firstSelectablePosition(grid);
  return position === null ? null : { rowKey, position };
}

/**
 * Naechste Zelle fuer eine Pfeiltaste. `ArrowUp`/`ArrowDown` wechseln die Zeile (am Rand bleibt
 * die aktuelle Zelle stehen), `ArrowLeft`/`ArrowRight` springen zur naechsten nicht
 * ausgeschlossenen Position in dieser Richtung (am Rand ebenfalls unveraendert). Jede andere Taste
 * gibt `current` unveraendert zurueck.
 */
export function nextCell(current: MatrixCellRef, key: string, grid: MatrixGrid): MatrixCellRef {
  const rowIndex = grid.rowKeys.indexOf(current.rowKey);

  if (key === 'ArrowUp' || key === 'ArrowDown') {
    if (rowIndex === -1) {
      return current;
    }
    const nextRowIndex = key === 'ArrowUp' ? rowIndex - 1 : rowIndex + 1;
    const nextRowKey = grid.rowKeys[nextRowIndex];
    if (nextRowKey === undefined) {
      return current;
    }
    return { rowKey: nextRowKey, position: current.position };
  }

  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    const direction = key === 'ArrowRight' ? 1 : -1;
    const nextPosition = nextSelectablePosition(grid, current.position, direction);
    return nextPosition === null ? current : { rowKey: current.rowKey, position: nextPosition };
  }

  return current;
}

/**
 * Gleicht eine Zelle gegen das aktuelle Raster ab -- etwa nach dem Ausschliessen einer Messung
 * (Punkt 4) oder wenn eine Zeile weggefallen ist. Existiert die Zeile nicht mehr oder ist die
 * Position ausgeschlossen, wird die naechste nicht ausgeschlossene Position gesucht (erst rechts,
 * dann links) in einer gueltigen Zeile; findet sich keine, gilt `firstCell(grid)`.
 */
export function normalizeCell(current: MatrixCellRef, grid: MatrixGrid): MatrixCellRef | null {
  const rowKey = grid.rowKeys.includes(current.rowKey) ? current.rowKey : grid.rowKeys[0];
  if (rowKey === undefined) {
    return null;
  }
  if (isSelectablePosition(grid, current.position)) {
    return { rowKey, position: current.position };
  }
  const right = nextSelectablePosition(grid, current.position, 1);
  if (right !== null) {
    return { rowKey, position: right };
  }
  const left = nextSelectablePosition(grid, current.position, -1);
  if (left !== null) {
    return { rowKey, position: left };
  }
  return firstCell(grid);
}
